import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertTriangle, Plug, Link2, RefreshCw, Store, Inbox, ShieldAlert, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CAP_COLS = [
  ["requested", "Requested"], ["app_approved", "App Approved"], ["seller_granted", "Seller Granted"],
  ["implemented", "Implemented"], ["live_verified", "Live Verified"],
];

const BRAND = { tiktok: "TikTok Shop", shopee: "Shopee", lazada: "Lazada" };

// Marketplace OAuth cannot run inside an iframe (providers deny framing) and we must never mint live state
// from the preview origin. When embedded (Emergent preview or live frame), route the owner to the standalone
// production ERP in a normal browser tab to start OAuth there.
const LIVE_ERP_ORIGIN = "https://erp.fasfone.com.my";
const IS_FRAMED = (() => { try { return window.self !== window.top; } catch { return true; } })();

function Flag({ on }) {
  return on ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" data-testid="cap-yes" />
            : <XCircle className="w-4 h-4 text-slate-300 inline" data-testid="cap-no" />;
}

function StatusBadge({ p }) {
  const map = {
    connected: ["text-emerald-700 border-emerald-300 bg-emerald-50", "Connected"],
    not_connected: ["text-amber-700 border-amber-300 bg-amber-50", "Not Connected"],
    setup_required: ["text-sky-700 border-sky-300 bg-sky-50", "Setup Required"],
    secrets_missing: ["text-rose-700 border-rose-300 bg-rose-50", "Secrets Missing"],
    error: ["text-rose-700 border-rose-300 bg-rose-50", "Token Expired"],
  };
  const [cls, label] = map[p.status] || map.not_connected;
  return <Badge variant="outline" className={cls} data-testid={`mp-status-${p.platform}`}>{label}</Badge>;
}

function PlatformCard({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [popupUrl, setPopupUrl] = useState(null);

  function openStandalone() {
    // Embedded (Emergent preview or live frame): open the standalone production ERP in a real browser tab.
    // No authorize call here -> no live state is minted from the preview origin.
    const url = `${LIVE_ERP_ORIGIN}/marketplace-connections`;
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) { setPopupUrl(url); toast.message("Popup blocked — use the link below to open the live ERP."); }
    else { toast.message("Opening the live ERP in a new tab to connect securely."); }
  }

  async function connect() {
    if (IS_FRAMED) { openStandalone(); return; }   // standalone branch below keeps state/sign/callback intact
    setBusy(true);
    try {
      const r = await api.get(`/marketplace/${p.platform}/authorize`);
      if (r.data?.url) { window.location.href = r.data.url; return; }
      toast.error(r.data?.error || "Unable to start authorization");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function sync(shopId) {
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/sync`, { platform: p.platform, shop_id: shopId });
      const d = r.data || {};
      toast.success(`Sync ${d.status}: ${d.orders_ingested ?? 0} new order(s), ${d.pages_done ?? 0} page(s)`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`mp-conn-${p.platform}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-sky-600" />
          <h3 className="font-semibold">{BRAND[p.platform] || p.platform}</h3>
          <Badge variant="outline" className="text-xs">{p.environment}</Badge>
        </div>
        <StatusBadge p={p} />
      </div>
      <dl className="text-sm space-y-1.5">
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Production callback</dt>
          <dd className="font-mono text-xs text-slate-700 text-right break-all">{p.callback_url || p.callback_path}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Secrets configured</dt>
          <dd><Flag on={p.secrets_configured} /></dd></div>
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Encryption key ready</dt>
          <dd><Flag on={p.encryption_ready} /></dd></div>
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Callback origin verified</dt>
          <dd><Flag on={p.callback_origin_configured} /></dd></div>
        <div className="flex justify-between gap-3"><dt className="text-slate-500">Last successful sync</dt>
          <dd className="text-slate-400 text-xs">{p.last_successful_sync || "—"}</dd></div>
      </dl>

      {p.shops?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`mp-shops-${p.platform}`}>
          {p.shops.map((s) => (
            <div key={s.shop_id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 p-2"
              data-testid={`mp-shop-${p.platform}-${s.shop_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Store className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.shop_name || s.shop_id}</div>
                  <div className="text-xs text-slate-400">{s.region || "—"} · token {s.token_status}</div>
                </div>
              </div>
              <button disabled={busy} data-testid={`mp-sync-${p.platform}-${s.shop_id}`}
                onClick={() => sync(s.shop_id)}
                className="h-8 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />Sync
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid={`mp-setup-${p.platform}`}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{p.setup_next_step}</span>
      </div>
      {IS_FRAMED && (
        <div data-testid={`mp-framed-note-${p.platform}`} className="mt-3 flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-2">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Marketplace sign-in can't run inside the embedded preview. Open the live ERP in a normal browser tab to connect securely.</span>
        </div>
      )}
      <button disabled={busy || (!IS_FRAMED && !p.can_connect)} data-testid={`mp-connect-${p.platform}`}
        onClick={connect}
        className={`mt-3 w-full h-10 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${(IS_FRAMED || p.can_connect) ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
        <Link2 className="w-4 h-4" />{IS_FRAMED ? "Open live ERP to connect" : (p.shops?.length ? "Reconnect / Add Shop" : "Connect")}
      </button>
      {popupUrl && (
        <a data-testid={`mp-open-live-fallback-${p.platform}`} href={popupUrl} target="_blank" rel="noopener noreferrer"
          className="mt-2 block text-center text-xs text-sky-700 underline">Open live ERP to connect ↗</a>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs min-w-[520px]">
          <thead className="text-slate-500"><tr>
            <th className="text-left p-1.5">Capability</th>
            {CAP_COLS.map(([k, l]) => <th key={k} className="p-1.5">{l}</th>)}
          </tr></thead>
          <tbody>
            {p.capabilities.map((c) => (
              <tr key={c.capability} className="border-t border-slate-100" data-testid={`cap-${p.platform}-${c.capability}`}>
                <td className="p-1.5 text-slate-700">{c.label}</td>
                {CAP_COLS.map(([k]) => <td key={k} className="p-1.5 text-center"><Flag on={c[k]} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function _d(epoch) {
  if (!epoch) return "—";
  try { return new Date(epoch * 1000).toISOString().slice(0, 10); } catch { return "—"; }
}

function OrderImportPanel({ platforms, imports, onChanged }) {
  const [busy, setBusy] = useState("");
  const byKey = Object.fromEntries((imports || []).map((s) => [`${s.platform}:${s.shop_id}`, s]));
  const stores = [];
  for (const p of platforms) for (const s of (p.shops || [])) stores.push({ platform: p.platform, shop: s });
  if (!stores.length) return null;

  async function act(url, platform, shop_id, key) {
    setBusy(key);
    try {
      const r = await api.post(url, { platform, shop_id });
      toast.success(r.data?.status ? `Import: ${r.data.status}` : "Done");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="order-import-panel">
      <h3 className="font-semibold mb-1">Automatic order import</h3>
      <p className="text-xs text-slate-500 mb-3">Read-only, order-only. Resumable all-status history backfill, then ongoing new/updated polling. No stock/price/shipment writes.</p>
      <div className="space-y-2">
        {stores.map(({ platform, shop }) => {
          const st = byKey[`${platform}:${shop.shop_id}`];
          const key = `${platform}:${shop.shop_id}`;
          const counts = st?.counts || {};
          return (
            <div key={key} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm" data-testid={`import-store-${platform}-${shop.shop_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-medium">{BRAND[platform]} · {shop.shop_name || shop.shop_id}</div>
                <Badge variant="outline" data-testid={`import-status-${platform}`}>
                  {st ? `${st.phase} / ${st.status}` : "not started"}
                </Badge>
              </div>
              {st && (
                <div className="text-xs text-slate-500 mt-1" data-testid={`import-coverage-${platform}`}>
                  Coverage {_d(st.coverage_earliest)} → {_d(st.coverage_latest)} ·
                  ins {counts.inserted || 0} / upd {counts.updated || 0} / unch {counts.unchanged || 0} / pages {counts.pages || 0}
                  {st.earliest_history_unknown ? " · earlier history UNKNOWN (not imported)" : ""}
                  {st.floor_note ? <span className="block text-slate-400 mt-0.5">{st.floor_note}</span> : null}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <button disabled={busy === key} data-testid={`import-run-${platform}`}
                  onClick={() => act("/marketplace/backfill/start", platform, shop.shop_id, key)}
                  className="h-8 px-3 rounded-md text-xs font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                  {st && st.phase === "incremental" ? "Run poll now" : "Start / Resume backfill"}
                </button>
                <button disabled={busy === key} data-testid={`import-enable-${platform}`}
                  onClick={() => act("/marketplace/jobs/activate-orders", platform, shop.shop_id, key)}
                  className="h-8 px-3 rounded-md text-xs font-medium border border-slate-300 hover:bg-white disabled:opacity-50">
                  Enable auto polling
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MarketplaceConnections() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [imports, setImports] = useState(null);

  function load() {
    api.get("/marketplace/connections").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/marketplace/sync-jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/marketplace/backfill/status").then((r) => setImports(r.data?.stores || [])).catch(() => {});
  }

  useEffect(() => {
    load();
    // Surface OAuth return status (?tiktok=success|error / ?shopee=... / ?lazada=...)
    const qs = new URLSearchParams(window.location.search);
    for (const plat of ["tiktok", "shopee", "lazada"]) {
      const v = qs.get(plat);
      if (v === "success") toast.success(`${plat} authorized successfully`);
      else if (v === "error") toast.error(`${plat} authorization failed: ${qs.get("reason") || "unknown"}`);
    }
    if (qs.get("tiktok") || qs.get("shopee") || qs.get("lazada")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;
  return (
    <div className="p-6 space-y-5" data-testid="marketplace-connections-page">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
        <h1 className="text-2xl font-bold">Connections &amp; Setup</h1>
        <p className="text-sm text-slate-500 mt-1">{data.note}</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {data.platforms.map((p) => <PlatformCard key={p.platform} p={p} onChanged={load} />)}
      </div>
      <OrderImportPanel platforms={data.platforms} imports={imports} onChanged={load} />
      <UnattendedSyncPanel jobs={jobs} onChanged={load} />
      {can("marketplace:webhooks_view") && <WebhookDiagnosticsPanel can={can} />}
    </div>
  );
}

function UnattendedSyncPanel({ jobs, onChanged }) {
  async function ensure() {
    try { const r = await api.post("/marketplace/sync-jobs/ensure"); toast.success(`${r.data.created} job(s) created (disabled)`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function toggle(j) {
    try { await api.post(`/marketplace/sync-jobs/${j.id}/${j.enabled ? "disable" : "enable"}`); toast.success(j.enabled ? "Disabled" : "Enabled (runs only when worker installed)"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  const r = jobs?.readiness;
  const chip = (on, label) => (
    <Badge variant="outline" className={on ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-slate-500 border-slate-300 bg-slate-50"} data-testid={`sync-ready-${label}`}>
      {on ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : <XCircle className="w-3 h-3 mr-1 inline" />}{label}
    </Badge>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="unattended-sync-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2"><RefreshCw className="w-4 h-4 text-indigo-500" />Unattended read-only sync</h2>
        <button onClick={ensure} data-testid="sync-ensure-btn" className="text-xs h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50">Create jobs for connected stores</button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">{r?.note || "Disabled by default. No store auth or marketplace writes. Runs only when the worker is installed."}</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {chip(r?.code_ready, "code-ready")}
        {chip(r?.scheduler_installed, "scheduler-installed")}
        {chip(r?.worker_running, "worker-running")}
        {r?.last_heartbeat_at
          ? <Badge variant="outline">last heartbeat {Math.round(r.heartbeat_age_seconds)}s ago</Badge>
          : <Badge variant="outline" className="text-slate-500">no worker heartbeat yet</Badge>}
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs"><thead><tr className="text-left text-slate-400 border-b"><th className="py-1">Store</th><th>Module</th><th>Enabled</th><th>Status</th><th>Last success</th><th>Fails</th><th></th></tr></thead>
          <tbody>
            {(jobs?.jobs || []).length === 0 ? <tr><td colSpan={7} className="py-3 text-slate-400">No jobs yet. Click "Create jobs for connected stores".</td></tr> :
              jobs.jobs.map((j) => (
                <tr key={j.id} className="border-b last:border-0" data-testid={`sync-job-${j.platform}-${j.module}`}>
                  <td className="py-1.5">{j.platform} · {j.shop_id}</td>
                  <td className="capitalize">{j.module.replace("_", " ")}</td>
                  <td>{j.enabled ? <span className="text-emerald-600">on</span> : <span className="text-slate-400">off</span>}</td>
                  <td><span className={j.status === "failed" ? "text-rose-600" : j.status === "partial" ? "text-amber-600" : "text-slate-600"}>{j.status}</span>{j.stale && <span className="ml-1 text-amber-600">· stale</span>}{j.disabled_reason && <span className="ml-1 text-slate-400">({j.disabled_reason})</span>}</td>
                  <td className="text-slate-500">{j.last_success_at ? new Date(j.last_success_at).toLocaleString() : "—"}</td>
                  <td className="text-center">{j.consecutive_failures || 0}</td>
                  <td className="text-right"><button onClick={() => toggle(j)} data-testid={`sync-toggle-${j.id}`} className="text-[11px] h-7 px-2 rounded border border-slate-200 hover:bg-slate-50">{j.enabled ? "Disable" : "Enable"}</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const STATE_CLS = {
  received: "text-slate-600", retry: "text-amber-600", processing: "text-sky-600",
  reconciled: "text-emerald-600", quarantined: "text-violet-600", dead_letter: "text-rose-600",
};

function WebhookDiagnosticsPanel({ can }) {
  const [inbox, setInbox] = useState(null);
  const [dead, setDead] = useState(null);
  const [busy, setBusy] = useState("");
  const canRecover = can("marketplace:webhooks_recover");

  function load() {
    api.get("/marketplace/webhook/inbox").then((r) => setInbox(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/marketplace/webhook/dead-letter").then((r) => setDead(r.data)).catch(() => {});
  }
  useEffect(() => { load(); }, []);

  async function recover(eventId) {
    setBusy(eventId);
    try {
      const r = await api.post("/marketplace/webhook/dead-letter/recover", { event_id: eventId });
      toast.success(`Recovery: ${r.data?.status || "requeued"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  }

  const verifiers = inbox?.verifiers || [];
  const summary = inbox?.summary || {};
  const rows = inbox?.rows || [];
  const deadRows = dead?.rows || [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="webhook-diagnostics-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Inbox className="w-4 h-4 text-indigo-500" />Inbound webhook inbox</h2>
        <button onClick={load} data-testid="webhook-refresh-btn" className="text-xs h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />Refresh</button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">Read-only. Raw-body signatures are verified before anything is stored; reconciliation only coalesces an already-enabled read job — never a marketplace write. Scoped to connected stores.</p>

      <div className="flex flex-wrap gap-2 mt-3" data-testid="webhook-verifiers">
        {verifiers.map((v) => (
          <Badge key={v.platform} variant="outline"
            className={v.available ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-slate-500 border-slate-300 bg-slate-50"}
            data-testid={`webhook-verifier-${v.platform}`}>
            {v.available ? <CheckCircle2 className="w-3 h-3 mr-1 inline" /> : <XCircle className="w-3 h-3 mr-1 inline" />}
            {BRAND[v.platform] || v.platform} verifier {v.available ? "ready" : "disabled"}
          </Badge>
        ))}
      </div>
      {verifiers.some((v) => !v.available) && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 space-y-0.5" data-testid="webhook-verifier-reasons">
          {verifiers.filter((v) => !v.available).map((v) => (
            <div key={v.platform}><span className="font-medium">{BRAND[v.platform] || v.platform}:</span> {v.reason}</div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3" data-testid="webhook-state-summary">
        {Object.keys(summary).length === 0
          ? <span className="text-xs text-slate-400">No webhook events received yet.</span>
          : Object.entries(summary).map(([state, n]) => (
            <Badge key={state} variant="outline" className={STATE_CLS[state] || "text-slate-600"} data-testid={`webhook-count-${state}`}>
              {state.replace("_", " ")}: {n}
            </Badge>
          ))}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs min-w-[640px]">
          <thead><tr className="text-left text-slate-400 border-b"><th className="py-1">Store</th><th>Event type</th><th>State</th><th>Reason</th><th>Attempts</th><th>Received</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="py-3 text-slate-400">No events in the inbox for connected stores.</td></tr> :
              rows.slice(0, 50).map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`webhook-event-${r.id}`}>
                  <td className="py-1.5">{r.platform} · {r.shop_id}</td>
                  <td>{r.event_type || "—"}</td>
                  <td><span className={STATE_CLS[r.state] || "text-slate-600"}>{r.state}</span></td>
                  <td className="text-slate-500">{r.quarantine_reason || r.last_error || "—"}</td>
                  <td className="text-center">{r.attempts || 0}</td>
                  <td className="text-slate-400">{r.received_at ? new Date(r.received_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-semibold flex items-center gap-1.5 text-rose-700"><ShieldAlert className="w-3.5 h-3.5" />Dead-letter ({deadRows.length})</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs min-w-[640px]" data-testid="webhook-dead-letter-table">
            <thead><tr className="text-left text-slate-400 border-b"><th className="py-1">Store</th><th>Event type</th><th>Attempts</th><th>Last error</th><th>Dead-lettered</th><th></th></tr></thead>
            <tbody>
              {deadRows.length === 0 ? <tr><td colSpan={6} className="py-3 text-slate-400">No dead-lettered events.</td></tr> :
                deadRows.map((r) => (
                  <tr key={r.event_id} className="border-b last:border-0" data-testid={`webhook-dead-${r.event_id}`}>
                    <td className="py-1.5">{r.platform} · {r.shop_id}</td>
                    <td>{r.event_type || "—"}</td>
                    <td className="text-center">{r.attempts || 0}</td>
                    <td className="text-rose-600">{r.last_error || "—"}</td>
                    <td className="text-slate-400">{r.dead_lettered_at ? new Date(r.dead_lettered_at).toLocaleString() : "—"}</td>
                    <td className="text-right">
                      {canRecover
                        ? <button disabled={busy === r.event_id} onClick={() => recover(r.event_id)} data-testid={`webhook-recover-${r.event_id}`}
                            className="text-[11px] h-7 px-2 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
                            <RotateCcw className={`w-3 h-3 ${busy === r.event_id ? "animate-spin" : ""}`} />Recover
                          </button>
                        : <span className="text-[10px] text-slate-400">no permission</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!canRecover && deadRows.length > 0 && (
          <p className="text-[11px] text-slate-400 mt-1" data-testid="webhook-recover-denied-note">Recovery requires the <code>marketplace:webhooks_recover</code> permission.</p>
        )}
      </div>
    </div>
  );
}
